import {Switch, Table, TablePaginationConfig, Typography} from 'antd';
import {Key, useCallback, useEffect, useMemo} from 'react';
import {generatePath} from 'react-router-dom';

import {appRoute} from '../../../../app-route';
import {renderCellActions} from '../../../../layout/actions-cell/actions-cell';
import {useLicenses} from '../../../../provider/license/license-hook';
import {Locale} from '../../../../provider/locale/locale';
import {AutoReplyRuleTypeEnum, AutoReplyToReviewItemType} from '../../../../service/reviews/reviews-response-templates';
import {FeaturesEnum} from '../../../../service/user/user-type';
import {useUrl} from '../../../../util/url-hook/url-hook';
import {ValidityColumn} from '../validity-column/validity-column';

import {autoRepliesTableColumnList} from './auto-replies-table-const';
import {AutoReplyToReviewTableRowDataType} from './auto-replies-table-type';
import * as styles from './auto-replies-table.scss';

const {Text} = Typography;

type PropsType = {
    autoRepliesToReviewsResult: Array<AutoReplyToReviewItemType>;
    count: number;
    setAutoReplyToReviewDataList: (autoReplyToReviewDataList: Array<AutoReplyToReviewItemType>) => void;
    setSelectedIdList: (selectedIdList: Array<string>) => void;
    pagination: TablePaginationConfig;
    setPagination: (pagination: TablePaginationConfig) => void;
    autoRepliesToReviewsIsInProgress: boolean;
    onDelete: (autoReplyToReviewData: AutoReplyToReviewItemType) => void;
    changeCondition: (autoReplyToReviewData: AutoReplyToReviewItemType, value: boolean) => void;
};

export function AutoRepliesTable(props: PropsType): JSX.Element {
    const {
        setSelectedIdList,
        setAutoReplyToReviewDataList,
        autoRepliesToReviewsResult,
        count,
        pagination,
        setPagination,
        changeCondition,
        autoRepliesToReviewsIsInProgress,
        onDelete,
    } = props;
    const {pushState} = useUrl();

    const {reviewsManagementAutoReplyEdit: reviewsManagementAutoReplyEditRoute} = appRoute;
    const {licenses} = useLicenses();

    const convertCompanyTableRow = useCallback(
        (autoReplyToReviewData: AutoReplyToReviewItemType): AutoReplyToReviewTableRowDataType => {
            const {name, description, id, mode, enabled, enabledFrom, enabledTo, templateSelectionStrategy, ruleType} =
                autoReplyToReviewData;

            const isEditAvailable =
                templateSelectionStrategy === 'gpt'
                    ? licenses[FeaturesEnum.gptAutoReplyLicence]?.isActive
                    : licenses[FeaturesEnum.reviewSuggests]?.isActive;

            function onChangeClick() {
                pushState(generatePath(reviewsManagementAutoReplyEditRoute.path, {autoReplyId: id}), {
                    ruleType: ruleType ?? AutoReplyRuleTypeEnum.Company,
                });
            }

            return {
                title: (
                    <>
                        {name}
                        {description && (
                            <Text className={styles.auto_replies_table__description} disabled key="description">
                                {description}
                            </Text>
                        )}
                    </>
                ),
                reply: (
                    <Locale
                        stringKey={
                            templateSelectionStrategy === 'gpt'
                                ? 'AUTO_REPLIES__FORM__REPLY_TYPE__NEURALLINK'
                                : 'RESPONSE_TEMPLATES__TEMPLATE'
                        }
                    />
                ),
                type: (
                    <Locale
                        stringKey={
                            mode === 'manual'
                                ? 'AUTO_REPLIES__REPLY_TYPE__TEMPORARY'
                                : 'AUTO_REPLIES__REPLY_TYPE__PERMANENT'
                        }
                    />
                ),
                condition: (
                    <Switch
                        checked={enabled}
                        onChange={(value: boolean) => {
                            changeCondition(autoReplyToReviewData, value);
                        }}
                    />
                ),
                validity: <ValidityColumn dateFrom={enabledFrom} dateTo={enabledTo} />,
                key: String(id),
                actions: renderCellActions(onChangeClick, () => onDelete(autoReplyToReviewData), !isEditAvailable),
            };
        },
        [licenses, pushState, reviewsManagementAutoReplyEditRoute.path, changeCondition, onDelete]
    );

    const dataList = useMemo(() => {
        return autoRepliesToReviewsResult || [];
    }, [autoRepliesToReviewsResult]);

    const tableDataList = useMemo(() => {
        return dataList.map(convertCompanyTableRow);
    }, [dataList, convertCompanyTableRow]);

    useEffect(() => {
        setAutoReplyToReviewDataList(dataList);
    }, [dataList, setAutoReplyToReviewDataList]);

    function handleRowSelection(_selectedRowKeys: Array<Key>, rowDataList: Array<AutoReplyToReviewTableRowDataType>) {
        setSelectedIdList(rowDataList.map((rowData: AutoReplyToReviewTableRowDataType): string => rowData.key));
    }

    return (
        <Table<AutoReplyToReviewTableRowDataType>
            columns={autoRepliesTableColumnList}
            dataSource={tableDataList}
            loading={autoRepliesToReviewsIsInProgress}
            onChange={setPagination}
            pagination={{
                size: 'default',
                showSizeChanger: true,
                hideOnSinglePage: false,
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: count,
            }}
            rowSelection={{columnWidth: 47, onChange: handleRowSelection}}
            scroll={{x: true}}
            size="middle"
        />
    );
}
